<template>
    <report-tab :name="name" title="Model Report" :icon="$icons.reportGears"
                :loading="loading" element-loading-text="Generating report"
                :url-help="$urls.HELP_REPORTS" :click-x="clickX">
        <h3>Table of Contents</h3>
        <ol>
            <li class="toc-bullet">
                <el-link type="primary" href="#shaft-parts">Shafts</el-link>
                <ol v-for="(part, i) in shaftParts" :key="i">
                    <li class="toc-sub-bullet">
                        <el-link type="primary" :href="part.href">{{ part.label }}</el-link>
                    </li>
                </ol>
            </li>
            <li class="toc-bullet">
                <el-link type="primary" href="#carrier-parts">Carriers</el-link>
                <ol v-for="(part, i) in carrierParts" :key="i">
                    <li class="toc-sub-bullet">
                        <el-link type="primary" :href="part.href">{{ part.label }}</el-link>
                    </li>
                </ol>
            </li>
            <li class="toc-bullet">
                <el-link type="primary" href="#gear-parts">Gear Parts</el-link>
                <ol v-for="(part, i) in gearParts" :key="i">
                    <li class="toc-sub-bullet">
                        <el-link type="primary" :href="part.href">{{ part.label }}</el-link>
                    </li>
                </ol>
            </li>
            <li class="toc-bullet">
                <el-link type="primary" href="#gear-meshes">Gear Meshes</el-link>
                <ol v-for="(conn, i) in meshes" :key="i">
                    <li class="toc-sub-bullet">
                        <el-link type="primary" :href="hrefMesh(i)">{{ conn.label }}</el-link>
                    </li>
                </ol>
            </li>
        </ol>
        <hr>
        <report-part id="shaft-parts" v-show="!loading" :parts="shaftParts" :typename="$requests.model.TYPENAME.PART.FRAME"/>
        <hr>
        <report-part id="carrier-parts" v-show="!loading" :parts="carrierParts" :typename="$requests.model.TYPENAME.PART.FRAME" :carrier="true"/>
        <hr>
        <report-part id="gear-parts" v-show="!loading" :parts="gearParts" :typename="$requests.model.TYPENAME.PART.GEAR_DETAIL"/>
        <hr>
        <div id="gear-meshes" v-show="!loading">
            <span v-if="meshes.length === 0" class="no-data">
                No gear meshes in model.
            </span>
            <div v-else v-for="(mesh, i) in meshes" :key="i">
                <h3 :id="idMesh(i)">
                    {{ mesh.label }}
                    &nbsp;&nbsp;&nbsp;
                    <el-button class="collapse-btn" @click="collapseMesh(mesh)" icon="el-icon-minus" type="text"/>
                    <el-button class="collapse-btn" @click="expandMesh(mesh)" icon="el-icon-plus" type="text"/>
                </h3>
                <el-collapse class="collapse-section" v-model="activeMeshSections">
                    <LogTable v-if="mesh.table.log.length > 0" :table="mesh.table.log"/>
                    <el-collapse-item :name="collapseName('Common', mesh)">
                        <span slot="title" class="collapse-title">Common</span>
                        <report-table-params :table="mesh.table.common"/>
                    </el-collapse-item>
                    <el-collapse-item :name="collapseName('Gear1', mesh)">
                        <span slot="title" class="collapse-title">{{ mesh.titleGear1 }}</span>
                        <report-table-params :table="mesh.table.gear1"/>
                    </el-collapse-item>
                    <el-collapse-item :name="collapseName('Gear2', mesh)">
                        <span slot="title" class="collapse-title">{{ mesh.titleGear2 }}</span>
                        <report-table-params :table="mesh.table.gear2"/>
                    </el-collapse-item>
                    <!--region PLACEHOLDER {feature} 2D view of meshing gears in transverse plane; likely use pixi.js-->
                    <!--<el-collapse-item :name="collapseName('Diagram', mesh)">-->
                    <!--<span slot="title" class="collapse-title">Diagram</span>-->
                    <!--</el-collapse-item>-->
                    <!--endregion-->
                </el-collapse>
            </div>
        </div>
    </report-tab>
</template>

<script>
  import ReportTab from "./ReportTab";
  import ReportTableParams from "./ReportTableParams";
  import ReportPart from "./ReportPart";
  import LogTable from "./LogTable";
  import {copyDeep, sortByString} from "../../assets/js/utils";

  export default {
    name: "ReportModel",
    components: {ReportTab, ReportPart, LogTable, ReportTableParams},
    props: {
      name: String,  // unique tab name
      clickX: Function,
    },

    units: {
      angle: 'deg',
    },
    collapseNames: {
      mesh: ['Common', 'Gear1', 'Gear2']
    },

    data() {
      return {
        // APOLLO
        gearParts: [],
        shaftParts: [],
        carrierParts: [],
        meshes: [],
        loading: true,

        // COLLAPSIBLE SECTIONS
        activeMeshSections: [],
      }
    },

    mounted() {
      // Called every time the report is opened.

      // query mesh data; intentionally not using await
      this.queryMeshes().then(() => window.MathJax.typeset());

      // map part data to local state
      this.mapParts();

      this.$nextTick(() => {
        this.loading = false;
        window.MathJax.typeset();
      });
    },
    methods: {
      // APOLLO
      async queryMeshes() {
        // API query to calculate all gear meshes, mapping the result to local state.

        // get ratio connections
        const fragment = this.$requests.model.fragmentPartLabel;
        const conns = this.$store.getters.getConnections(this.$requests.model.ConnectionType.RATIO, fragment);

        // calculate mesh properties
        const meshConns = await this.$store.dispatch('getMeshes', conns);

        // assign to local state
        this.meshes = meshConns.map(conn => {
          const parts = conn.parts;
          if (conn.mesh) this.$handlers.eng.convertMesh(conn.mesh, undefined, this.$options.units);

          return {
            Id: conn.Id,  // collapse naming
            label: this.$handlers.model.getConnLabel(conn),  // TOC and sorting
            titleGear1: this.$handlers.model.partLabel(parts[0]),  // collapse section title
            titleGear2: this.$handlers.model.partLabel(parts[1]),  // collapse section title
            table: {
              log: this.$handlers.eng.tableLog(conn.mesh),
              common: this.$handlers.eng.tableMeshMacro(conn.mesh, this.$options.units),
              gear1: this.$handlers.eng.tableMeshGear1(conn.mesh, this.$options.units),
              gear2: this.$handlers.eng.tableMeshGear2(conn.mesh, this.$options.units),
            }
          }
        });
        sortByString(this.meshes, 'label');
      },
      partFields(part) {
        // Helper method to map fields used by all part mappings.

        return {
          Id: part.Id,  // collapse naming
          id: this.$handlers.model.idPart(part),  // href target
          href: this.$handlers.model.hrefPart(part),  // href source
          label: this.$handlers.model.partLabel(part),  // TOC and sorting
          frame: this.$store.getters.getFrame(part),  // constraints message
        }
      },
      mapGearDetailPart(part) {
        // Map detailed gear part to local state.

        // deep copy gear object with tooth properties; avoids mutating Apollo cache with units conversion
        const gear = copyDeep(part.obj);
        gear.tooth = this.$handlers.eng.parseTooth(gear.tooth);

        // convert units
        this.$handlers.gear.convertGearGeom(gear, undefined, this.$options.units);
        this.$handlers.eng.convertTooth(gear.tooth, undefined, this.$options.units);

        // add rim diameter to tooth params; not included in backend
        gear.tooth.finishedParams.rim_diameter = gear.geom.rimDiameter;

        return {
          ...this.partFields(part),
          table: {
            assm: this.$handlers.model.tableAssm(part),
            conn: this.$handlers.model.tableConns(part),
            geom: this.$handlers.eng.tableFinishedTooth(gear.tooth, this.$options.units),
            file: this.$handlers.lib.tableFile(gear),
          }
        };
      },
      mapCarrierPart(part) {
        // Map carrier frame part to local state.

        return {
          ...this.partFields(part),
          table: {
            assm: this.$handlers.model.tableAssm(part),
            conn: this.$handlers.model.tableConns(part),
            geom: this.$handlers.frame.tableCarrierGeom(part.obj, this.$options.units),
          }
        };
      },
      mapShaftPart(part) {
        // Map shaft frame part to local state.

        return {
          ...this.partFields(part),
          table: {
            assm: this.$handlers.model.tableAssm(part),
            conn: this.$handlers.model.tableConns(part),
          }
        };
      },
      mapParts() {
        // Get parts from cache and map to local state for TOC and tabular display.

        // get all parts; should exclude ground
        const parts = this.$store.getters.getParts(this.$requests.model.partQueryStr);

        // map to local state
        let item;
        parts.forEach(part => {
          switch (part.__typename) {
            case this.$requests.model.TYPENAME.PART.GEAR_DETAIL:
              item = this.mapGearDetailPart(part);
              this.gearParts.push(item);
              break;
            case this.$requests.model.TYPENAME.PART.FRAME:
              if (this.$handlers.model.isPartFrameCarrier(part)) {
                item = this.mapCarrierPart(part);
                this.carrierParts.push(item);
              } else {
                item = this.mapShaftPart(part);
                this.shaftParts.push(item);
              }
              break;
          }
        });

        // order the same as feature tree
        sortByString(this.gearParts, 'label');
        sortByString(this.shaftParts, 'label');
        sortByString(this.carrierParts, 'label');
      },

      // TOC LINKS
      hrefMesh(index) {
        return '#mesh-' + index;
      },
      idMesh(index) {
        return 'mesh-' +index;
      },

      // COLLAPSE NAMES
      collapseName(name, item) {
        return name + item.Id;
      },
      collapseMeshNames(mesh) {
        return this.$options.collapseNames.mesh.map(name => this.collapseName(name, mesh));
      },

      // COLLAPSE CLICKS
      collapseMesh(mesh) {
        const removeNames = this.collapseMeshNames(mesh);
        this.activeMeshSections= this.activeMeshSections.filter(name => !removeNames.includes(name));
      },
      expandMesh(mesh) {
        this.activeMeshSections.push(...this.collapseMeshNames(mesh));
      },
    },
  }
</script>

<style scoped lang="scss" src="./assets/report-content.scss"/>
